import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';

// Funciones auxiliares para obtener datos con timeout
export const getDataWithTimeout = async (dataPromise, timeoutMs = 10000) => {
  try {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Tiempo de espera agotado')), timeoutMs);
    });

    return await Promise.race([dataPromise, timeoutPromise]);
  } catch (error) {
    if (error.message === 'Tiempo de espera agotado') {
      console.error('La operación tardó demasiado tiempo');
    }
    throw error;
  }
};

export const getInventoryData = async (userId) => {
  try {
    const inventoryPromise = new Promise(async (resolve, reject) => {
      try {
        const q = query(
          collection(db, 'products'),
          where('userId', '==', userId),
          orderBy('stock', 'asc'),
          limit(10)
        );
        const snapshot = await getDocs(q);
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        resolve(products);
      } catch (error) {
        reject(error);
      }
    });

    return await getDataWithTimeout(inventoryPromise);
  } catch (error) {
    console.error('Error al obtener inventario:', error);
    throw new Error('No se pudo obtener la información del inventario');
  }
};

export const getSalesData = async (userId, days = 7) => {
  try {
    const salesPromise = new Promise(async (resolve, reject) => {
      try {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        const q = query(
          collection(db, 'sales'),
          where('userId', '==', userId),
          where('date', '>=', startDate),
          orderBy('date', 'desc')
        );
        const snapshot = await getDocs(q);
        const sales = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        resolve(sales);
      } catch (error) {
        reject(error);
      }
    });

    return await getDataWithTimeout(salesPromise);
  } catch (error) {
    console.error('Error al obtener ventas:', error);
    throw new Error('No se pudo obtener la información de ventas');
  }
};

export const getLowStockProducts = async (userId) => {
  try {
    const lowStockPromise = new Promise(async (resolve, reject) => {
      try {
        const q = query(
          collection(db, 'products'),
          where('userId', '==', userId),
          where('stock', '<=', 10),
          orderBy('stock', 'asc'),
          limit(5)
        );
        const snapshot = await getDocs(q);
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        resolve(products);
      } catch (error) {
        reject(error);
      }
    });

    return await getDataWithTimeout(lowStockPromise);
  } catch (error) {
    console.error('Error al obtener productos con bajo stock:', error);
    throw new Error('No se pudo obtener la información de productos con bajo stock');
  }
};

export const searchProduct = async (userId, searchTerm) => {
  try {
    const q = query(
      collection(db, 'products'),
      where('userId', '==', userId),
      where('name', '>=', searchTerm),
      where('name', '<=', searchTerm + '\uf8ff'),
      limit(5)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error al buscar producto:', error);
    throw new Error('No se pudo buscar el producto');
  }
};

export const getTopSellingProducts = async (userId, days = 7) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const q = query(
      collection(db, 'sales'),
      where('userId', '==', userId),
      where('date', '>=', startDate),
      orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    const sales = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Agrupar productos por cantidad vendida
    const productSales = {};
    sales.forEach(sale => {
      if (sale.items) {
        sale.items.forEach(item => {
          if (!productSales[item.productId]) {
            productSales[item.productId] = {
              name: item.name,
              quantity: 0,
              total: 0
            };
          }
          productSales[item.productId].quantity += item.quantity;
          productSales[item.productId].total += item.price * item.quantity;
        });
      }
    });

    // Convertir a array y ordenar por cantidad
    return Object.values(productSales)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  } catch (error) {
    console.error('Error al obtener productos más vendidos:', error);
    throw new Error('No se pudo obtener los productos más vendidos');
  }
};

// Contexto general del POS
export const POS_CONTEXT = `
Este sistema POS (Punto de Venta) es una solución integral para la gestión de comercios. Permite:
- Registrar ventas y controlar caja
- Gestionar inventario y productos
- Administrar clientes y proveedores
- Generar reportes de ventas, inventario y caja
- Controlar usuarios y permisos
- Realizar cierres de turno y arqueos de caja
- Configurar impuestos, moneda y preferencias del sistema
- Exportar datos y consultar historial

Módulos principales:
1. Ventas: Registro rápido, búsqueda de productos, descuentos, tickets y facturación.
2. Inventario: Altas, bajas, ajustes, stock mínimo, alertas y valorización.
3. Productos: Catálogo, precios, códigos de barras, categorías y variantes.
4. Clientes: Registro, historial de compras, saldos y contacto.
5. Proveedores: Registro, historial de compras y cuentas por pagar.
6. Caja: Apertura, cierre, arqueo, ingresos y egresos.
7. Reportes: Ventas por fecha, producto, usuario, inventario, caja y más.
8. Usuarios: Roles, permisos y auditoría de acciones.
9. Configuración: Datos de la empresa, impuestos, moneda, impresión y más.
`;

export const FAQS = [
  {
    keywords: ['cómo registro una venta', 'registrar venta', 'hacer una venta', 'nueva venta', 'iniciar venta', 'cobrar'],
    answer: '🛒 Para registrar una venta, ve al módulo "Ventas" o "POS". Busca el producto por nombre, código o voz, agrégalo al carrito, selecciona el cliente (opcional), elige el método de pago y confirma. El sistema imprime el ticket automáticamente.'
  },
  {
    keywords: ['cómo agrego un producto', 'agregar producto', 'nuevo producto', 'crear producto', 'añadir producto'],
    answer: '📦 Para agregar un producto: ve a "Productos" → "+ Agregar producto". Completa nombre, precio, costo, stock inicial y categoría. Si tienes código de barras, puedes escanearlo. Guarda para que aparezca en ventas.'
  },
  {
    keywords: ['cómo consulto el inventario', 'consultar inventario', 'ver inventario', 'inventario actual'],
    answer: '📊 En el módulo "Inventario" ves todos tus productos con stock actual, alertas de bajo stock y valor total. Puedes filtrar por categoría o buscar por nombre. Usa "stock bajo" para ver los que necesitan reposición.'
  },
  {
    keywords: ['cómo modifico un precio', 'modificar precio', 'cambiar precio', 'actualizar precio'],
    answer: '💲 Para cambiar un precio: ve a "Productos", busca el producto, haz clic en el ícono de edición ✏️, actualiza el precio y guarda. El cambio se refleja inmediatamente en nuevas ventas.'
  },
  {
    keywords: ['cómo cierro turno', 'cierre de turno', 'cerrar caja', 'arqueo', 'cierre del día'],
    answer: '💵 Para cerrar turno: ve a "Caja" → "Cerrar turno". Verás el resumen de ventas, ingresos y egresos del turno. Confirma el arqueo y se generará el reporte automáticamente.'
  },
  {
    keywords: ['cómo registro un cliente', 'agregar cliente', 'nuevo cliente', 'registrar cliente'],
    answer: '👤 Para agregar un cliente: ve a "Clientes" → "+ Nuevo cliente". Ingresa nombre, teléfono, correo y RNC si aplica. Una vez registrado, puedes asociarlo a ventas para llevar historial de compras.'
  },
  {
    keywords: ['cómo consulto ventas por fecha', 'ventas por fecha', 'reporte de ventas', 'historial de ventas'],
    answer: '📅 En "Reportes" puedes filtrar ventas por fecha, vendedor o método de pago. Verás totales, transacciones y detalles de productos vendidos. Exporta en Excel o PDF.'
  },
  {
    keywords: ['cómo exporto datos', 'exportar datos', 'descargar reporte', 'excel', 'pdf'],
    answer: '📤 Desde "Reportes" o "Inventario", usa el botón de descarga (⬇️) para exportar en Excel o PDF. Puedes exportar ventas, clientes, productos o movimientos de inventario.'
  },
  {
    keywords: ['configuracion', 'ajustes', 'cambiar moneda', 'datos empresa', 'preferencias', 'configurar sistema'],
    answer: '⚙️ En "Configuración" puedes: cambiar la moneda (RD$, $, €), configurar impuestos (ITBIS 18%), actualizar los datos de tu empresa, subir tu logo y configurar la impresora térmica.'
  },
  {
    keywords: ['reparacion', 'arreglo', 'servicio tecnico', 'celular', 'laptop', 'taller', 'dispositivo'],
    answer: '🛠️ El módulo de Reparaciones te permite: registrar equipos recibidos, indicar el problema, asignar técnico, seguir el estado (Recibido → En proceso → Listo → Entregado) y cobrar al finalizar.'
  },
  {
    keywords: ['itbis', 'impuestos', 'iva', 'ncf', 'factura fiscal', 'comprobante fiscal'],
    answer: '🧾 El ITBIS (18%) se configura en Ajustes y se aplica automáticamente en facturas. Para NCF (comprobante fiscal), el sistema genera la secuencia según el tipo de comprobante seleccionado.'
  },
  {
    keywords: ['imprimir recibo', 'imprimir ticket', 'impresora', 'ticketera', 'ticket'],
    answer: '🖨️ Al finalizar una venta, haz clic en "Imprimir". Configura tu impresora térmica en Ajustes → Impresora. Compatible con impresoras de 58mm y 80mm por USB o Bluetooth.'
  },
  {
    keywords: ['que es pos', 'que es pdv', 'como hago una venta', 'punto de venta'],
    answer: '🏪 El POS (Punto de Venta) es el corazón del sistema. Aquí puedes: buscar productos por voz o código, armar el carrito, aplicar descuentos, cobrar en efectivo/tarjeta y emitir recibos.'
  },
  {
    keywords: ['qué módulos tiene el sistema', 'módulos del sistema', 'funcionalidades', 'que puedo hacer'],
    answer: POS_CONTEXT
  },
  {
    keywords: ['soporte', 'contacto', 'problema', 'error del sistema'],
    answer: '🆘 Para soporte técnico, documenta el error y contacta al administrador del sistema. También puedes revisar los logs en Configuración → Auditoría.'
  },
  {
    keywords: ['stock bajo', 'poco stock', 'se acaba', 'agotado', 'quedan pocos'],
    answer: '⚠️ Los productos con stock bajo aparecen marcados en el inventario. Ve a "Inventario" → filtro "Stock bajo" para ver la lista completa y planificar reposición.'
  },
  {
    keywords: ['descuento', 'aplicar descuento', 'precio especial', 'rebaja', 'oferta'],
    answer: '🏷️ En el POS, selecciona el producto en el carrito y haz clic en el ícono de descuento. Puedes aplicar un porcentaje o monto fijo. También puedes configurar promociones automáticas en el módulo de Promociones.'
  },
  {
    keywords: ['proveedor', 'proveedores', 'compra de mercancia', 'orden de compra'],
    answer: '🏢 En "Proveedores" registras tus suplidores. Puedes registrar compras de mercancía, lo que actualiza el inventario automáticamente. También lleva historial de pagos pendientes a proveedores.'
  },
  {
    keywords: ['caja chica', 'gastos', 'egreso', 'salida de dinero'],
    answer: '💸 En "Caja" puedes registrar egresos (gastos del día) y llevar control de la caja chica. Todo queda registrado en el cierre de turno y en los reportes financieros.'
  },
  {
    keywords: ['codigo de barras', 'barcode', 'escanear', 'lector'],
    answer: '📱 Puedes usar el lector de código de barras conectado al sistema o la cámara del dispositivo. Al escanear, el producto se agrega automáticamente al carrito si existe en el inventario.'
  },
];

// Función para normalizar texto (eliminar tildes y pasar a minúsculas)
export function normalizeText(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export const getLocalResponse = async (message, context, userId) => {
  const command = normalizeText(message.trim());

  try {
    // Respuestas automáticas para preguntas frecuentes
    for (const faq of FAQS) {
      if (faq.keywords.some(k => command.includes(normalizeText(k)))) {
        // Mejorar formato visual de la respuesta
        let formatted = faq.answer
          .replace(/\n/g, '\n')
          .replace(/\* /g, '• ')
          .replace(/\"/g, '"')
          .replace(/\-/g, '–');
        // Agregar emoji si es una acción principal
        if (faq.keywords.some(k => k.includes('venta'))) formatted = '🛒 ' + formatted;
        if (faq.keywords.some(k => k.includes('producto'))) formatted = '📦 ' + formatted;
        if (faq.keywords.some(k => k.includes('cliente'))) formatted = '👤 ' + formatted;
        if (faq.keywords.some(k => k.includes('proveedor'))) formatted = '🏢 ' + formatted;
        if (faq.keywords.some(k => k.includes('caja') || k.includes('turno'))) formatted = '💵 ' + formatted;
        if (faq.keywords.some(k => k.includes('reporte'))) formatted = '📊 ' + formatted;
        if (faq.keywords.some(k => k.includes('soporte') || k.includes('ayuda'))) formatted = '🆘 ' + formatted;
        return {
          text: formatted,
          suggestions: [
            { text: "Ver inventario", value: "ver inventario" },
            { text: "Ver ventas", value: "ver ventas" },
            { text: "Ayuda", value: "ayuda" },
          ]
        };
      }
    }

    // Si pregunta por módulos, funcionalidades o contexto general
    if (command.includes('módulos') || command.includes('funcionalidades') || command.includes('qué puede hacer')) {
      return {
        text: POS_CONTEXT,
        suggestions: [
          { text: "Ver inventario", value: "ver inventario" },
          { text: "Ver ventas", value: "ver ventas" },
          { text: "Ayuda", value: "ayuda" },
        ]
      };
    }

    // Comandos básicos que no requieren datos
    const isGreeting = /^(hola|hi|buenos|buenas|hey|saludos|que tal|qué tal)/i.test(command);
    if (isGreeting) {
      const greetings = [
        '¡Hola! Soy tu asistente de Posent. Puedes pedirme: buscar productos, ver ventas, stock bajo, resumen de turno o navegar a cualquier módulo.',
        '¡Hola! Estoy listo para ayudarte. Di el nombre de un producto para buscarlo, o pregúntame sobre ventas, inventario o clientes.',
        'Hola, soy el asistente de voz de Posent. Puedo buscar productos por voz, agregar al carrito, ver reportes y mucho más. ¿Qué necesitas?',
        '¡Hola! ¿Quieres buscar un producto, ver inventario, cerrar turno o revisar ventas del día?'
      ];
      return {
        text: greetings[Math.floor(Math.random() * greetings.length)],
        suggestions: [
          { text: "Ver inventario", value: "ver inventario" },
          { text: "Ver ventas", value: "ver ventas" },
          { text: "Stock bajo", value: "stock bajo" },
        ]
      };
    }

    // Comandos de inventario
    if (command.includes('inventario') || command.includes('productos')) {
      const inventory = await getInventoryData(userId);
      if (!inventory || inventory.length === 0) {
        return {
          text: 'No hay productos en el inventario actualmente. ¿Deseas agregar uno nuevo?',
          suggestions: [
            { text: "Agregar producto", value: "agregar producto" },
            { text: "Ver ventas", value: "ver ventas" },
          ]
        };
      }

      const totalProducts = inventory.length;
      const totalStock = inventory.reduce((sum, item) => sum + (item.stock || 0), 0);
      const totalValue = inventory.reduce((sum, item) => sum + ((item.price || 0) * (item.stock || 0)), 0);

      return {
        text: `📊 Resumen del Inventario:\n\n` +
          `• Total de productos: ${totalProducts}\n` +
          `• Total de unidades: ${totalStock}\n` +
          `• Valor total del inventario: $${totalValue.toFixed(2)}\n\n` +
          `Productos en inventario:\n${inventory.map(item =>
            `• ${item.name}: ${item.stock} unidades (${item.price ? `$${item.price}` : 'Precio no disponible'})`
          ).join('\n')}`,
        suggestions: [
          { text: "Ver ventas", value: "ver ventas" },
          { text: "Stock bajo", value: "stock bajo" },
          { text: "Agregar producto", value: "agregar producto" },
        ]
      };
    }

    // Comandos de ventas
    if (command.includes('ventas') || command.includes('venta')) {
      const salesData = await getSalesData(userId);
      if (!salesData || salesData.length === 0) {
        return {
          text: 'No hay ventas registradas en los últimos 7 días. ¿Deseas registrar una nueva venta?',
          suggestions: [
            { text: "Nueva venta", value: "nueva venta" },
            { text: "Ver inventario", value: "ver inventario" },
          ]
        };
      }

      const total = salesData.reduce((sum, sale) => sum + (sale.total || 0), 0);
      const averageSale = total / salesData.length;
      const todaySales = salesData.filter(sale => {
        const saleDate = sale.date?.toDate ? sale.date.toDate() : new Date(sale.date);
        const today = new Date();
        return saleDate.toDateString() === today.toDateString();
      });
      const todayTotal = todaySales.reduce((sum, sale) => sum + (sale.total || 0), 0);

      return {
        text: `💰 Resumen de Ventas:\n\n` +
          `• Total de ventas (7 días): $${total.toFixed(2)}\n` +
          `• Número de transacciones: ${salesData.length}\n` +
          `• Promedio por venta: $${averageSale.toFixed(2)}\n` +
          `• Ventas de hoy: $${todayTotal.toFixed(2)}\n\n` +
          `¿Necesitas información más específica?`,
        suggestions: [
          { text: "Ver inventario", value: "ver inventario" },
          { text: "Nueva venta", value: "nueva venta" },
          { text: "Stock bajo", value: "stock bajo" },
        ]
      };
    }

    // Comandos de ayuda
    if (command.includes('ayuda') || command.includes('help') || command.includes('cómo')) {
      return {
        text: '📚 Guía de Comandos:\n\n' +
          '1. Inventario:\n' +
          '   - "ver inventario": Muestra todos los productos\n' +
          '   - "stock bajo": Muestra productos con poco stock\n' +
          '   - "buscar [producto]": Busca un producto específico\n\n' +
          '2. Ventas:\n' +
          '   - "ver ventas": Muestra ventas recientes\n' +
          '   - "ventas de hoy": Muestra ventas del día\n' +
          '   - "total de ventas": Muestra el total de ventas\n\n' +
          '3. Sistema:\n' +
          '   - "cómo vender": Instrucciones para registrar una venta\n' +
          '   - "cómo agregar producto": Instrucciones para agregar productos\n' +
          '   - "cómo modificar precio": Instrucciones para cambiar precios',
        suggestions: [
          { text: "Ver inventario", value: "ver inventario" },
          { text: "Ver ventas", value: "ver ventas" },
          { text: "Stock bajo", value: "stock bajo" },
        ]
      };
    }

    // Búsqueda de productos
    if (command.startsWith('buscar') || command.startsWith('encontrar')) {
      const searchTerm = command.split(' ').slice(1).join(' ');
      if (!searchTerm) {
        return {
          text: 'Por favor, especifica qué producto deseas buscar. Por ejemplo: "buscar leche"',
          suggestions: [
            { text: "Ver inventario", value: "ver inventario" },
            { text: "Ver ventas", value: "ver ventas" },
          ]
        };
      }

      const results = await searchProduct(userId, searchTerm);
      if (!results || results.length === 0) {
        return {
          text: `No se encontraron productos que coincidan con "${searchTerm}".`,
          suggestions: [
            { text: "Ver inventario", value: "ver inventario" },
            { text: "Ver ventas", value: "ver ventas" },
          ]
        };
      }

      return {
        text: `🔍 Resultados de búsqueda para "${searchTerm}":\n\n${results.map(item =>
          `• ${item.name}: ${item.stock} unidades (${item.price ? `$${item.price}` : 'Precio no disponible'})`
        ).join('\n')}`,
        suggestions: [
          { text: "Ver inventario", value: "ver inventario" },
          { text: "Ver ventas", value: "ver ventas" },
        ]
      };
    }

    // Productos más vendidos
    if (command.includes('más vendidos') || command.includes('top ventas')) {
      const topProducts = await getTopSellingProducts(userId);
      if (!topProducts || topProducts.length === 0) {
        return {
          text: 'No hay datos de ventas suficientes para mostrar los productos más vendidos.',
          suggestions: [
            { text: "Ver inventario", value: "ver inventario" },
            { text: "Ver ventas", value: "ver ventas" },
          ]
        };
      }

      return {
        text: `🏆 Productos más vendidos (últimos 7 días):\n\n${topProducts.map((item, index) =>
          `${index + 1}. ${item.name}: ${item.quantity} unidades vendidas ($${item.total.toFixed(2)})`
        ).join('\n')}`,
        suggestions: [
          { text: "Ver inventario", value: "ver inventario" },
          { text: "Ver ventas", value: "ver ventas" },
        ]
      };
    }

    // Mensaje por defecto si no se reconoce el comando
    const fallbacks = [
      'No estoy seguro de qué necesitas. Puedes decirme el nombre de un producto para buscarlo, o usar: "ver ventas", "inventario", "stock bajo", "resumen de turno" o "ayuda".',
      'No entendí esa consulta. Prueba diciendo el nombre de un producto directamente, o pregunta sobre ventas, clientes o inventario.',
      'Mmm, no lo capturo bien. Di el nombre del producto que buscas, o prueba: "ver ventas hoy", "stock bajo", "cerrar turno" o "ayuda" para más opciones.'
    ];
    return {
      text: fallbacks[Math.floor(Math.random() * fallbacks.length)],
      suggestions: [
        { text: "Ver inventario", value: "ver inventario" },
        { text: "Ver ventas", value: "ver ventas" },
        { text: "Stock bajo", value: "stock bajo" },
        { text: "Ayuda", value: "ayuda" },
      ]
    };

  } catch (error) {
    console.error('Error en respuesta local:', error);
    throw new Error(
      error.message === 'Tiempo de espera agotado'
        ? 'La consulta está tardando demasiado. Por favor, intenta nuevamente.'
        : 'No se pudo procesar tu consulta. Por favor, intenta con un comando más simple como "hola", "inventario" o "ventas".'
    );
  }
};

// 1. Función para predecir ventas de la próxima semana
export const predictNextWeekSales = async (userId) => {
  try {
    const salesPromise = new Promise(async (resolve, reject) => {
      try {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30); // Últimos 30 días
        const q = query(
          collection(db, 'sales'),
          where('userId', '==', userId),
          where('date', '>=', startDate),
          orderBy('date', 'desc')
        );
        const snapshot = await getDocs(q);
        const sales = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        resolve(sales);
      } catch (error) {
        reject(error);
      }
    });
    const salesData = await getDataWithTimeout(salesPromise);
    if (!salesData || salesData.length === 0) return 0;
    // Agrupar ventas por semana
    const weeks = {};
    salesData.forEach(sale => {
      const saleDate = sale.date?.toDate ? sale.date.toDate() : new Date(sale.date);
      const week = format(saleDate, 'yyyy-ww');
      if (!weeks[week]) weeks[week] = 0;
      weeks[week] += sale.total || 0;
    });
    const weekTotals = Object.values(weeks);
    const avg = weekTotals.reduce((a, b) => a + b, 0) / weekTotals.length;
    return avg;
  } catch (error) {
    return 0;
  }
};

// 2. Función para predecir productos que se agotarán pronto
export const predictLowStockSoon = async (userId) => {
  try {
    const productsPromise = new Promise(async (resolve, reject) => {
      try {
        const q = query(
          collection(db, 'products'),
          where('userId', '==', userId),
          where('stock', '<=', 20),
          orderBy('stock', 'asc'),
          limit(10)
        );
        const snapshot = await getDocs(q);
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        resolve(products);
      } catch (error) {
        reject(error);
      }
    });
    const products = await getDataWithTimeout(productsPromise);
    return products;
  } catch (error) {
    return [];
  }
};

// 3. Comandos inteligentes y resúmenes automáticos por ruta
export const getSmartCommandResponse = async (command, userId, location) => {
  const cmd = command.toLowerCase();
  
  if (cmd.includes('reporte de ventas') || cmd.includes('total de ventas') || cmd.includes('ventas de la semana')) {
    const salesData = await getSalesData(userId, 7);
    const total = salesData.reduce((sum, sale) => sum + (sale.total || 0), 0);
    return {
      text: `📝 Reporte de ventas (últimos 7 días):\n- Total vendido: $${total.toFixed(2)}\n- Transacciones: ${salesData.length}`,
      suggestions: [
        { text: 'Ver inventario', value: 'ver inventario' },
        { text: 'Stock bajo', value: 'stock bajo' },
      ]
    };
  }
  
  if (cmd.includes('agrega producto') || cmd.includes('nuevo producto') || cmd.includes('crear producto')) {
    return {
      text: 'Para agregar un producto, ve al catálogo de Productos y haz clic en el botón "+ Agregar Producto".',
      suggestions: [
        { text: 'Ir a Productos', value: 'productos' },
      ]
    };
  }
  
  if (cmd.includes('resumen de turno') || cmd.includes('cierres') || cmd.includes('arqueo')) {
    const salesData = await getSalesData(userId, 1);
    const total = salesData.reduce((sum, sale) => sum + (sale.total || 0), 0);
    return {
      text: `🔒 Resumen de caja de hoy:\n- Ventas registradas hoy: $${total.toFixed(2)}\n- Transacciones: ${salesData.length}`,
      suggestions: [
        { text: 'Ver ventas', value: 'ver ventas' },
      ]
    };
  }
  
  if (cmd.includes('predicción de ventas') || cmd.includes('predecir ventas') || cmd.includes('pronostico')) {
    const pred = await predictNextWeekSales(userId);
    return {
      text: `🔮 Predicción: Se estima que venderás aproximadamente $${pred.toFixed(2)} la próxima semana (basado en el promedio de los últimos 30 días).`,
      suggestions: [
        { text: 'Ver ventas', value: 'ver ventas' },
      ]
    };
  }
  
  if (cmd.includes('agotarán pronto') || cmd.includes('agotarse pronto') || cmd.includes('sin stock pronto')) {
    const products = await predictLowStockSoon(userId);
    if (!products.length) return { text: 'No se detectan productos en riesgo de agotarse pronto.', suggestions: [] };
    return {
      text: `⚠️ Productos en riesgo de agotarse:\n${products.map(p => `• ${p.name}: ${p.stock} unidades en stock (límite de alerta: 20)`).join('\n')}`,
      suggestions: [
        { text: 'Ver inventario', value: 'ver inventario' },
      ]
    };
  }
  
  if (location && location.includes('/sales')) {
    const salesData = await getSalesData(userId, 1);
    const total = salesData.reduce((sum, sale) => sum + (sale.total || 0), 0);
    return {
      text: `Llevas $${total.toFixed(2)} en ventas hoy. ¿Quieres ver un reporte detallado o realizar otra acción?`,
      suggestions: [
        { text: 'Reporte de ventas', value: 'reporte de ventas' },
      ]
    };
  }
  
  if (location && location.includes('/inventory')) {
    const inventory = await getInventoryData(userId);
    return {
      text: `Tienes ${inventory.length} productos registrados. ¿Deseas ver cuáles están con stock bajo?`,
      suggestions: [
        { text: 'Stock bajo', value: 'stock bajo' },
      ]
    };
  }
  return null;
};

// 4. Función principal que unifica la respuesta de IA (OpenAI o Local)
export const getAssistantResponse = async (message, context, userRole, userId, chatHistory = []) => {
  try {
    // 1. Comandos inteligentes y resúmenes automáticos
    const smartResp = await getSmartCommandResponse(message.toLowerCase(), userId, context);
    if (smartResp) return smartResp;

    // 2. Prompt mejorado para OpenAI
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY?.trim();
    if (!apiKey) {
      return await getLocalResponse(message, context, userId);
    }

    // Recopilar contexto adicional según la ruta
    let additionalInfo = '';
    if (context && context.includes('/sales')) {
      const salesData = await getSalesData(userId, 1);
      const total = salesData.reduce((sum, sale) => sum + (sale.total || 0), 0);
      additionalInfo += `\nHoy: $${total.toFixed(2)} en ventas.`;
    }
    if (context && (context.includes('/inventory') || context.includes('/products'))) {
      const inventory = await getInventoryData(userId);
      additionalInfo += `\nInventario actual: ${inventory.length} productos.`;
    }
    if (context && context.includes('/customers')) {
      additionalInfo += '\nEstás en la sección de clientes.';
    }
    
    // Historial conversacional (últimos 3 mensajes)
    const pastMessages = (chatHistory || []).slice(-3).map(m => ({
      role: m.type === 'user' ? 'user' : 'assistant',
      content: m.text
    }));
    
    const systemPrompt = `Eres Posent IA, el asistente inteligente de voz del sistema POS Posent.\n` +
      `El usuario tiene el rol: ${userRole || 'cajero/dueño'}.\n` +
      `Sección actual: ${context}.\n` +
      `\nEres experto en:\n` +
      `• POS/Punto de venta: buscar productos, agregar al carrito, cobrar, emitir tickets\n` +
      `• Inventario: stock, ajustes, alertas de bajo stock, valorización\n` +
      `• Ventas: reportes, totales del día, cierres de turno, historial\n` +
      `• Clientes: registro, historial de compras, fidelización\n` +
      `• Reparaciones: ingreso de equipo, seguimiento de estado, cobro\n` +
      `• Facturación: ITBIS, NCF, comprobantes fiscales (República Dominicana)\n` +
      `• Configuración: moneda, impresora, datos de empresa\n` +
      `\nIMPORTANTE: Responde SIEMPRE en español, de forma concisa (máximo 2-3 líneas). ` +
      `Si el usuario dice un nombre de producto, explícale que puede buscarlo por voz directamente. ` +
      `Sé natural y útil como un compañero de trabajo, no como un bot genérico.`;
      
    try {
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            ...pastMessages,
            { role: 'user', content: message }
          ],
          temperature: 0.7,
          maxValues: 500,
        },
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );
      if (!response.data?.choices?.[0]?.message?.content) {
        throw new Error('Respuesta inválida del servidor');
      }
      const text = response.data.choices[0].message.content.trim();
      const suggestions = [
        { text: "Ver inventario", value: "ver inventario" },
        { text: "Ventas recientes", value: "ver ventas" },
        { text: "Stock bajo", value: "stock bajo" },
      ];
      return {
        text,
        suggestions
      };
    } catch (axiosError) {
      if (axiosError.response?.status === 401) {
        throw new Error('La clave API de OpenAI no es válida. Por favor, verifica la configuración.');
      } else if (axiosError.response?.status === 429) {
        throw new Error('Se ha excedido el límite de solicitudes. Por favor, espera unos minutos.');
      } else if (axiosError.code === 'ECONNABORTED') {
        throw new Error('La solicitud ha tardado demasiado. Por favor, intenta nuevamente.');
      }
      throw new Error('Error al comunicarse con el servicio de IA. Por favor, intenta más tarde.');
    }
  } catch (error) {
    return await getLocalResponse(message, context, userId);
  }
};
